/* Homepage opening: the photograph settles into its approved framing, then
   the copy appears. Without JavaScript or with less motion, show the final state. */
(() => {
  'use strict';
  const root = document.documentElement;
  const hero = document.querySelector('[data-hero-entrance]');
  const motion = matchMedia('(prefers-reduced-motion: reduce)');
  const pending = 'hero-entrance-pending';
  const running = 'hero-entrance-running';
  const ctaDelay = 1800;
  const interactive = 'a, button, input, select, textarea, summary, [role="button"], [contenteditable]';
  if (!hero || !root.classList.contains(pending) || motion.matches ||
      window.Shopify?.designMode || !hero.animate) {
    root.classList.remove(pending, running);
    return;
  }

  const events = new AbortController();
  const scrollEvents = new AbortController();
  const animations = [];
  const timers = new Set();
  const waitCleanups = [];
  let stopped = false;
  let splashObserver;

  function releaseScroll() {
    root.classList.remove(running);
    scrollEvents.abort();
  }

  function finish() {
    if (stopped) return;
    stopped = true;
    splashObserver?.disconnect();
    timers.forEach(clearTimeout);
    waitCleanups.forEach(cleanup => cleanup());
    animations.forEach(animation => animation.cancel());
    root.classList.remove(pending);
    releaseScroll();
    events.abort();
  }

  function delay(callback, milliseconds) {
    const timer = setTimeout(() => {
      timers.delete(timer);
      callback();
    }, milliseconds);
    timers.add(timer);
    return timer;
  }

  function holdScroll() {
    root.classList.add(running);
    // Keep native pinch / browser zoom, and release immediately for intentional
    // navigation or form interaction. Ordinary scrolling waits for the CTA.
    window.addEventListener('wheel', event => {
      if (!event.ctrlKey) event.preventDefault();
    }, { passive: false, capture: true, signal: scrollEvents.signal });
    window.addEventListener('touchmove', event => {
      if (event.touches.length === 1) event.preventDefault();
    }, { passive: false, capture: true, signal: scrollEvents.signal });
    document.addEventListener('keydown', event => {
      if (!stopped && !event.ctrlKey && !event.metaKey && !event.altKey &&
          ['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '].includes(event.key)) {
        event.preventDefault();
      }
    }, { capture: true, signal: scrollEvents.signal });
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) finish();
    }, { signal: events.signal });
    window.addEventListener('resize', finish, { signal: events.signal });
    // A paused/failed animation must never leave the page locked.
    delay(finish, 5000);
  }

  function photographReady() {
    const background = hero.querySelector('[data-hero-bg]');
    const source = background && getComputedStyle(background).backgroundImage.match(/^url\(["']?(.*?)["']?\)$/);
    if (!source) return Promise.resolve();
    return new Promise(resolve => {
      const image = new Image();
      const ready = () => {
        image.onload = image.onerror = null;
        clearTimeout(timeout);
        timers.delete(timeout);
        resolve();
      };
      const timeout = delay(ready, 2500);
      waitCleanups.push(ready);
      image.onload = () => {
        if (image.decode) image.decode().catch(() => {}).then(ready);
        else ready();
      };
      image.onerror = ready;
      image.src = source[1];
    });
  }

  function splashFinished() {
    return new Promise(resolve => {
      function check() {
        if (!root.classList.contains(pending)) finish();
        if (stopped || !root.classList.contains('is-splashing')) {
          splashObserver?.disconnect();
          resolve();
        }
      }
      splashObserver = new MutationObserver(check);
      splashObserver.observe(root, { attributes: true, attributeFilter: ['class'] });
      waitCleanups.push(resolve);
      check();
    });
  }

  // Interruptions reveal the content immediately, including keyboard focus and
  // returning from another page. Theme-editor replacements do not replay it.
  window.addEventListener('pagehide', finish, { signal: events.signal });
  window.addEventListener('pageshow', event => { if (event.persisted) finish(); }, { signal: events.signal });
  window.addEventListener('beforeprint', finish, { signal: events.signal });
  motion.addEventListener('change', finish, { signal: events.signal });
  // Register interaction exits before waiting for the image: a cart/menu opened
  // during loading must not get locked when the photograph becomes ready.
  document.addEventListener('focusin', finish, { signal: events.signal });
  document.addEventListener('keydown', event => {
    if (event.key === 'Escape' || event.key === 'Tab' || event.target.closest?.(interactive)) finish();
  }, { capture: true, signal: events.signal });
  ['pointerdown', 'click'].forEach(name => {
    document.addEventListener(name, event => {
      if (event.target.closest?.(interactive)) finish();
    }, { capture: true, signal: events.signal });
  });
  ['shopify:section:load', 'shopify:section:unload', 'shopify:section:select'].forEach(name => {
    document.addEventListener(name, event => {
      if (!hero.isConnected || event.target.contains(hero)) finish();
    }, { signal: events.signal });
  });

  Promise.all([photographReady(), splashFinished()]).then(() => {
    if (stopped) return;
    const rect = hero.getBoundingClientRect();
    if (!root.classList.contains(pending) || !hero.isConnected ||
        rect.bottom <= 0 || rect.top >= innerHeight || window.scrollY > 1) {
      finish();
      return;
    }
    splashObserver?.disconnect();
    const sequence = [
      ['.hero__eyebrow', 1000, 1000, 8],
      ['.hero__heading', 1000, 1200, 12],
      ['.hero__subheading', 1500, 1000, 8],
      ['.hero__ctas', ctaDelay, 750, 6],
      ['.night-edition', ctaDelay, 750, 0]
    ];
    // Read the final CSS (including the subheading's softer opacity), then
    // install the first frames in the same task, before the browser paints.
    root.classList.remove(pending);
    holdScroll();
    const photograph = hero.querySelector('.hero__bg');
    const image = hero.querySelector('[data-hero-bg]');
    if (photograph && image) {
      const mobile = matchMedia('(max-width: 768px)').matches;
      // Fade the wrapper while the image gently pulls back. The hero clips the
      // enlarged edges; neither filters nor permanent cropping alter the asset.
      animations.push(photograph.animate([
        { opacity: 0 },
        { opacity: getComputedStyle(photograph).opacity }
      ], { duration: 1800, easing: 'cubic-bezier(.4,0,.2,1)', fill: 'backwards' }));
      animations.push(image.animate([
        { transform: `scale(${mobile ? 1.035 : 1.065})` },
        { transform: getComputedStyle(image).transform }
      ], { duration: 3400, easing: 'cubic-bezier(.2,.65,.3,1)', fill: 'backwards' }));
    }
    sequence.forEach(([selector, wait, duration, distance]) => {
      hero.querySelectorAll(selector).forEach(element => {
        const opacity = getComputedStyle(element).opacity;
        animations.push(element.animate([
          { opacity: 0, transform: `translateY(${distance}px)` },
          { opacity, transform: 'translateY(0)' }
        ], { delay: wait, duration, easing: 'cubic-bezier(.22,1,.36,1)', fill: 'backwards' }));
      });
    });
    // The CTA's entrance releases scrolling while the photograph keeps settling.
    delay(releaseScroll, ctaDelay);
    // Finishing/cancelling returns to the normal visible CSS.
    return Promise.all(animations.map(animation => animation.finished)).then(finish);
  }).catch(finish);
})();
